import { FC, useMemo } from 'react'
import Button from 'components/features/pfm/Button/Button'
import styles from 'components/features/premium/SubscriptionPanel/SubscriptionPanel.styl'
import { StripeForm } from 'components/features/pfm/StripeForm/StripeForm'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { StripeRoot } from 'components/features/pfm/StripeRoot/StripeRoot'
import { MONTHLY_SUBSCRIPTION_FEE, YEARLY_SUBSCRIPTION_FEE } from 'constants/subscription'
import Flex from 'components/atoms/Flex/Flex'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { Link } from 'react-router-dom'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { featureFlagSelector } from 'selectors/globalSelectors'
import {
  CC_ONBOARDING_STATE_CHECKING_ELIGIBILITY,
  CC_ONBOARDING_STATE_FAILED,
  CC_ONBOARDING_STATE_NOT_STARTED,
  CC_ONBOARDING_STATE_OFFER_RECEIVED,
} from 'components/features/creditCard/creditCardStore/constants'

export type PlanOption = 'monthly' | 'annual'

type SubscriptionPanelProps = {
  value: PlanOption
  onChange: (option: PlanOption) => void
  onClose: () => void
  onStripeReady: () => void
}
export const SubscriptionPanel: FC<SubscriptionPanelProps> = (props) => {
  const { value, onClose, onChange, onStripeReady } = props

  const optionsChild = useMemo(() => {
    return (
      <div className={ styles.plan }>
        <div className={ styles.button }>
          <OptionButton
            isActive={ value === 'monthly' }
            amount={ MONTHLY_SUBSCRIPTION_FEE }
            note='per month'
            onClick={ () => onChange('monthly') }
          />
          <div className={ styles.save }>&nbsp;</div>
        </div>
        <div className={ styles.button }>
          <OptionButton
            isActive={ value === 'annual' }
            amount={ YEARLY_SUBSCRIPTION_FEE }
            note='per year'
            onClick={ () => onChange('annual') }
          />
          <div className={ styles.save }>Save $40</div>
        </div>
      </div>
    )
  }, [value, onChange])

  return (
    <div className={ styles.subscriptionPanel }>
      <ApplyForCardSection />
      <h2>Select a plan</h2>
      Cancel anytime from the Settings screen.
      { optionsChild }
      <h2>Enter your payment details</h2>
      <div className={ styles.stripe }>
        <StripeRoot>
          <StripeForm
            onEscape={ onClose }
            onReady={ onStripeReady }
          />
        </StripeRoot>
      </div>
    </div>
  )
}

type OptionButtonProps = {
  isActive: boolean
  amount: number
  note: string
  onClick: () => void
}
const OptionButton: FC<OptionButtonProps> = (props) => {
  const { isActive, amount, note, onClick } = props

  return (
    <Button
      size='square'
      theme={ isActive ? 'advisorTopicSelected' : 'advisorTopic' }
      shadow='light'
      title={ formatUSCurrency(amount) }
      text={ note }
      onClick={ onClick }
    />
  )
}

export const ApplyForCardSection: FC<{}> = () => {
  const isCardFeatureEnabled = useStoreSelector((state) => featureFlagSelector(state, 'enable_credit_card'))
  const onboarding = useStoreSelector((state) => state.data.creditCard.onboarding)

  if (!isCardFeatureEnabled) {
    return null
  }
  const applicationNotStarted = onboarding?.state === CC_ONBOARDING_STATE_NOT_STARTED
  const offerReceived = onboarding?.state === CC_ONBOARDING_STATE_OFFER_RECEIVED
  const offerDenied = onboarding?.state === CC_ONBOARDING_STATE_FAILED
  const offerPending = onboarding?.state === CC_ONBOARDING_STATE_CHECKING_ELIGIBILITY

  const getCtaText = () => {
    if (offerReceived || offerPending || offerDenied) {
      return 'See Your Application'
    } else if (applicationNotStarted) {
      return 'Apply Now'
    } else {
      return 'Continue Card Application'
    }
  }

  return (
    <Flex>
      <h3>Get your Premium Membership for free with the Status Card</h3>
      <Link to='/credit-card/onboarding'>
        <Button text={ getCtaText() } />
      </Link>
      <NavigationLink to='creditcard'>
        <Button text='Learn More' style='textOnly' />
      </NavigationLink>
      <Flex component='p' fullWidth horizontallyCenter>
        or
      </Flex>
    </Flex>
  )
}

